import type { LoginResponseData, Maybe, Response, StoreThis } from '@/types'
import type { CustomerUser } from '@/types/customer/users'

import { ref } from 'vue'
import { handleErrors } from '@/helpers'

import { defineStore, storeToRefs } from 'pinia'
import { useUiStore } from '@/stores/ui'
import { useCustomerProfileStore } from '@/stores/customer/profile'

const resource = 'auth'
const storeId = `customer-${resource}`

export const useCustomerAuthStore = defineStore(storeId, () => {
  /**
   * На всякий случай не удаляем, чтобы точно инициализировалось хранилище ui
   * */
  const ui = useUiStore()
  /**
   * Первоначально authToken заполняется в хранилище ui, если обнаружен в localStorage
   * Хранилище ui в первый раз запускается в TheAppLayout (а в uiStore инициализируется authStore)
   * */
  const authToken = ref<Maybe<string>>(null)
  const authorized = ref(false)

  const customerProfileStore = useCustomerProfileStore()
  const { profile: currentUser } = storeToRefs(customerProfileStore)

  const isLoading = ref(false)

  async function sendPhone(
    this: StoreThis,
    phone: Maybe<string>,
    token?: Maybe<string>
  ) {
    let error = false
    let validationErrors: Record<string, string[]> = {}
    let otherError = ''

    isLoading.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.post(`/c/${resource}/login`, {
        'smart-token': token,
        phone,
      })

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }
    } catch (e: any) {
      error = true
      console.error(`[${storeId}/sendPhone]`, resource, e)

      const errors = e?.response?.data?.errors
      if (errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''
      }
    } finally {
      isLoading.value = false
    }

    return { error, validationErrors, otherError }
  }

  async function loginWithCode(this: StoreThis, code: string) {
    let error = false
    let validationErrors: Record<string, string[]> = {}
    let otherError = ''

    isLoading.value = true

    try {
      const {
        data: { success, errors, data },
      }: Response<LoginResponseData<CustomerUser>> = await this.rest.post(
        `/c/${resource}/login/code`,
        {
          code,
        }
      )

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }

      authToken.value = data.token
      currentUser.value = data.user

      await setAuthHeader.call(this)

      // authorized.value = true
    } catch (e: any) {
      error = true
      console.error(`[${storeId}/loginWithCode]`, resource, e)

      const errors = e?.response?.data?.errors
      if (errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''
      }
    } finally {
      isLoading.value = false
    }

    return { error, validationErrors, otherError }
  }

  async function logout(this: StoreThis) {
    authToken.value = null
    currentUser.value = null

    await setAuthHeader.call(this)

    authorized.value = false
  }

  async function setAuthHeader(this: StoreThis) {
    this.setToken(authToken.value)
  }

  return {
    authToken,
    authorized,

    currentUser,

    isLoading,

    sendPhone,
    loginWithCode,
    logout,

    setAuthHeader,
  }
})
